import React, { useState, useEffect } from "react";
import { useAuth, apiService } from "../services/api";
import { Plus, Shield, User, Copy, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";

const UsersManagementTab = () => {
  const { token, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [invitedCredentials, setInvitedCredentials] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    role: "user",
  });

  const fetchUsers = async () => {
    try {
      const response = await apiService.getUsers(token);
      setUsers(response.data);
    } catch (error) {
      toast.error("Erro ao carregar usuários");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [token]);

  const handleInvite = async (e) => {
    e.preventDefault();
    try {
      const response = await apiService.inviteUser(formData, token);
      setInvitedCredentials({
        email: response.data.email,
        password: response.data.temporary_password,
      });
      fetchUsers();
      setFormData({ name: "", email: "", role: "user" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao convidar usuário");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiado para a área de transferência!");
  };

  if (loading)
    return (
      <div className="p-8 text-center text-slate-500">
        Carregando usuários...
      </div>
    );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 font-heading">
            Equipe e Acessos
          </h3>
          <p className="text-sm text-slate-500">
            Gerencie quem tem acesso ao workspace da sua empresa.
          </p>
        </div>

        {currentUser?.role === "admin" && (
          <Button
            onClick={() => {
              setIsModalOpen(true);
              setInvitedCredentials(null);
            }}
            className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm flex items-center justify-center gap-2 w-full sm:w-auto"
          >
            <Plus className="w-4 h-4" />
            Convidar Usuário
          </Button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-slate-500 uppercase tracking-widest bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-4 font-medium">Usuário</th>
                <th className="px-6 py-4 font-medium">Função</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map((u) => (
                <tr key={u.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600 font-medium">
                      {u.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="font-medium text-slate-900">{u.name}</div>
                      <div className="text-slate-500">{u.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                        u.role === "admin"
                          ? "bg-violet-100 text-violet-700"
                          : "bg-slate-100 text-slate-700"
                      }`}
                    >
                      {u.role === "admin" ? (
                        <Shield className="w-3 h-3" />
                      ) : (
                        <User className="w-3 h-3" />
                      )}
                      {u.role === "admin" ? "Administrador" : "Usuário"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Dialog do Design System */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-md w-[95vw] sm:w-full max-h-[90vh] overflow-y-auto">
          {!invitedCredentials ? (
            <>
              <DialogHeader>
                <DialogTitle>Convidar novo usuário</DialogTitle>
                <DialogDescription>
                  Preencha os dados para gerar um convite seguro.
                </DialogDescription>
              </DialogHeader>

              <form
                id="invite-form"
                onSubmit={handleInvite}
                className="space-y-4 py-4"
              >
                <div className="space-y-2">
                  <Label>Nome Completo</Label>
                  <Input
                    required
                    placeholder="João da Silva"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>E-mail</Label>
                  <Input
                    type="email"
                    required
                    placeholder="joao@empresa.com.br"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Função no Sistema</Label>
                  <select
                    className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                    value={formData.role}
                    onChange={(e) =>
                      setFormData({ ...formData, role: e.target.value })
                    }
                  >
                    <option value="user">Usuário Padrão</option>
                    <option value="admin">Administrador</option>
                  </select>
                </div>
              </form>

              <DialogFooter className="gap-2 sm:gap-0 mt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsModalOpen(false)}
                  className="w-full sm:w-auto"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  form="invite-form"
                  className="bg-indigo-600 hover:bg-indigo-700 w-full sm:w-auto"
                >
                  Enviar Convite
                </Button>
              </DialogFooter>
            </>
          ) : (
            <div className="text-center space-y-4 py-6">
              <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold font-heading">
                Usuário Criado!
              </h3>
              <p className="text-sm text-slate-500">
                Envie as credenciais abaixo para o usuário acessar o sistema.
              </p>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-left relative group pr-12">
                <div className="text-sm break-all">
                  <span className="font-semibold text-slate-700 block sm:inline mr-1">
                    E-mail:
                  </span>
                  {invitedCredentials.email}
                </div>
                <div className="text-sm mt-2 break-all">
                  <span className="font-semibold text-slate-700 block sm:inline mr-1">
                    Senha Provisória:
                  </span>
                  <code className="bg-slate-200 px-2 py-0.5 rounded text-indigo-700 font-mono">
                    {invitedCredentials.password}
                  </code>
                </div>

                <button
                  onClick={() =>
                    copyToClipboard(
                      `Acesso Firebird BI\nE-mail: ${invitedCredentials.email}\nSenha: ${invitedCredentials.password}`,
                    )
                  }
                  className="absolute top-1/2 -translate-y-1/2 right-2 p-2 rounded-md bg-white border border-slate-200 text-slate-500 hover:text-indigo-600 shadow-sm opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                  title="Copiar credenciais"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>

              <Button
                className="w-full mt-6 bg-indigo-600 hover:bg-indigo-700"
                onClick={() => setIsModalOpen(false)}
              >
                Concluir
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersManagementTab;
